import React, { createContext, useContext, useState } from 'react';
import {
  Snackbar,
  Alert,
  Slide,
  IconButton,
  Typography,
  Box,
} from '@dewise-devex/backstage-plugin-mui-uikit-react';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Represents a notification object.
 *
 */
interface Notification {
  /** The unique identifier for the notification (optional). */
  id?: number;
  /** The message content of the notification. */
  message: string;
  /** The type of the notification. */
  type: 'success' | 'error' | 'delete' | 'info';
}

/**
 * Represents the notification context type.
 *
 */
interface NotificationContextType {
  /** Function to add a notification. */
  addNotification: (notification: Notification) => void;
}

/**
 * Represents the API response for notifications.
 *
 * @template ResponseType The type of the response data.
 */
export interface NotificationApiResponse<ResponseType> {
  /** The message content of the response. */
  message: string;
  /** The data returned by the API. */
  data: ResponseType;
  /** The error message, if any. */
  error?: string;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

const emojiMap: { [key in Notification['type']]: string } = {
  success: '✅',
  delete: '🗑️',
  error: '⚠️',
  info: 'ℹ️',
};

/**
 * A provider component that supplies the notification context to its children.
 * @param children The child components that will have access to the notification context.
 */
export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  /**
   * Adds a notification to the list of notifications.
   * @param notification The notification to add.
   */
  const addNotification = (notification: Notification) => {
    const id = new Date().getTime();
    setNotifications(prev => [...prev, { id, ...notification }]);

    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  /**
   * Removes a notification from the list of notifications.
   * @param id The ID of the notification to remove.
   */
  const removeNotification = (id: number) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ addNotification }}>
      {children}

      {notifications.map((notif, index, arr) => (
        <Snackbar
          key={notif.id}
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          TransitionComponent={props =>
            index === arr.length - 1 ? (
              <Slide {...props} direction="right" />
            ) : (
              <Slide {...props} direction="up" />
            )
          }
          style={{
            bottom: 60 + (arr.length - index - 1) * 65,
            paddingLeft: '14px',
          }}
        >
          <Alert
            icon={false}
            action={
              <IconButton
                size="small"
                color="inherit"
                onClick={() => removeNotification(notif.id!)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{
              backgroundColor: theme => theme.palette.inverse.surface,
              color: theme => theme.palette.surface.surface,
              fontSize: '1rem',
              width: '312px',
              padding: '6px 16px',
              borderRadius: '4px',
              boxShadow: theme => theme.shadows[6],
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ marginRight: '10px' }}>{emojiMap[notif.type]}</Box>
              <Typography
                variant="bodyMD"
                sx={{ color: theme => theme.palette.surface.surface }}
              >
                {notif.message}
              </Typography>
            </Box>
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

/**
 * A hook that provides access to the notification context.
 * @returns The notification context.
 * @throws Will throw an error if used outside of a NotificationProvider.
 */
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider',
    );
  }
  return context;
};

/**
 * A hook that allows you to invoke a service and display a notification based on the result.
 * @returns A function that can be used to invoke a service and display a notification based on the result.
 * @example
 * ```tsx
 * const invokeService = useApiNotifications();
 *
 * const handleDelete = async () => {
 *   await invokeService(
 *     () => deleteEntity(entityId),
 *     false
 *   );
 * };
 * ```
 */
export const useApiNotifications = () => {
  const { addNotification } = useNotifications();

  /**
   * A function that allows you to invoke a service and display a notification based on the result.
   * @param service The service to invoke.
   * @param disableSnackbar Whether to disable the snackbar notification.
   * @returns The data returned by the service.
   */
  const invokeService = async <T,>(
    service: () => unknown,
    disableSnackbarType?: 'disableOnSuccess' | 'disableOnError',
  ): Promise<NotificationApiResponse<T>> => {
    const enableSuccess = disableSnackbarType !== 'disableOnSuccess';
    const enableError = disableSnackbarType !== 'disableOnError';
    return await (service as () => Promise<NotificationApiResponse<T>>)()
      .then(res => {
        enableSuccess &&
          addNotification({
            message: res.message,
            type: 'success',
          });
        return res;
      })
      .catch((error: Error) => {
        enableError &&
          addNotification({
            message: error.message,
            type: 'error',
          });
        return {
          message: '',
          error: error.message,
          data: undefined as T,
        };
      });
  };

  return invokeService;
};
